import type { ButtonHTMLAttributes } from 'react';
import { useId, useState } from 'react';
import { variantColors, type ButtonVariant } from '../Button/variantColors';
import { createResponsiveTextStroke } from '@/styles/textStroke';
import { useClickWithSound } from '@/shared/hooks/useClickWithSound';

export interface TicketCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Color variant of the ticket */
  variant?: ButtonVariant;
  /** Main title text */
  title?: string;
  /** Subtitle text below the title */
  subtitle?: string;
  /** Icon element to display in the right stub area */
  icon?: React.ReactNode;
  /** Optional children for custom content (overrides title/subtitle/icon) */
  children?: React.ReactNode;
}

// Text styles with MindBoggle font and black stroke
const titleStyle: React.CSSProperties = {
  fontFamily: 'Mind Boggle, sans-serif',
  fontSize: 'clamp(1.25rem, 5cqw, 2rem)',
  fontWeight: 900,
  color: 'white',
  ...createResponsiveTextStroke(1, 0.05),
  textTransform: 'uppercase',
  lineHeight: 1.1
};

const subtitleStyle: React.CSSProperties = {
  fontFamily: 'Mind Boggle, sans-serif',
  fontSize: 'clamp(0.875rem, 3.5cqw, 1.25rem)',
  fontWeight: 700,
  color: 'white',
  ...createResponsiveTextStroke(0.5, 0.03),
  textTransform: 'uppercase',
  lineHeight: 1.2
};

// ViewBox: 0 0 1102 372 - Wide horizontal ticket with semi-circular notches on sides
// Based on the actual Figma design SVG

// Main ticket shape path - has curved notches on left and right edges
const MAIN_PATH =
  'M1065.91 80.6601V294.27H1065.82C1048.77 294.27 1034.95 308.09 1034.95 325.14C1034.95 328.58 1035.51 331.88 1036.55 334.98C1036.12 335 1035.7 335.01 1035.26 335.01H67.61C67.11 335.01 66.63 335.01 66.14 334.98C67.18 331.88 67.74 328.58 67.74 325.14C67.74 308.12 53.97 294.32 36.96 294.27V80.6601H37.05C54.1 80.6601 67.92 66.8301 67.92 49.7901C67.92 45.1201 66.88 40.6901 65.02 36.7201C65.87 36.6501 66.73 36.6101 67.61 36.6101H1035.26C1036.19 36.6101 1037.11 36.6501 1038.02 36.7301C1036.16 40.6901 1035.13 45.1201 1035.13 49.7801C1035.13 66.7901 1048.9 80.6001 1065.91 80.6501V80.6601Z';

export function TicketCard({
  className = '',
  variant = 'blue',
  title,
  subtitle,
  icon,
  children,
  disabled,
  onClick,
  ...props
}: TicketCardProps) {
  const [isPressed, setIsPressed] = useState(false);
  const gradientId = useId();
  const shadowId = useId();
  const colors = variantColors[variant];
  const activePress = isPressed && !disabled;
  const handleClick = useClickWithSound(onClick, disabled);

  // Render default content layout if title/subtitle/icon are provided
  const renderContent = () => {
    if (children) {
      return children;
    }

    if (title || subtitle || icon) {
      return (
        <>
          {/* Text content area - positioned before dashed line */}
          <div
            className="absolute flex flex-col justify-center overflow-hidden"
            style={{
              containerType: 'size',
              top: '15%',
              bottom: '15%',
              left: '5%',
              right: '35%'
            }}
          >
            {title && <p style={titleStyle}>{title}</p>}
            {subtitle && <p style={{ ...subtitleStyle, wordBreak: 'break-word' }}>{subtitle}</p>}
          </div>
          {/* Icon stub area - positioned after dashed line */}
          {icon && (
            <div
              className="absolute flex items-center justify-center"
              style={{
                top: '15%',
                bottom: '15%',
                left: '72%',
                right: '5%'
              }}
            >
              <div className="h-[70%] w-auto flex items-center justify-center">{icon}</div>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <button
      type="button"
      className={`relative transition-all duration-150 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      onMouseDown={() => {
        setIsPressed(true);
      }}
      onMouseUp={() => {
        setIsPressed(false);
      }}
      onMouseLeave={() => {
        setIsPressed(false);
      }}
      onTouchStart={() => {
        setIsPressed(true);
      }}
      onTouchEnd={() => {
        setIsPressed(false);
      }}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      <svg
        className="block size-full transition-transform duration-150"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 1102 372"
        style={{ transform: activePress ? 'translateY(3px)' : 'none' }}
        aria-hidden="true"
      >
        <defs>
          {/* Drop shadow filter for 3D effect */}
          <filter
            id={shadowId}
            x="-10%"
            y="-10%"
            width="120%"
            height="130%"
            filterUnits="objectBoundingBox"
          >
            <feDropShadow
              dx="8"
              dy="12"
              stdDeviation="6"
              floodColor={colors.shadow}
              floodOpacity="0.8"
            />
          </filter>

          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="551.44"
            x2="551.44"
            y1="337.65"
            y2="37.79"
          >
            <stop stopColor={colors.gradient.end} />
            <stop offset="0.16" stopColor={colors.gradient.middle} />
            <stop offset="0.63" stopColor={colors.gradient.middle} />
            <stop offset="1" stopColor={colors.gradient.start} />
          </linearGradient>
        </defs>

        {/* Main gradient fill with drop shadow */}
        <path d={MAIN_PATH} fill={`url(#${gradientId})`} filter={`url(#${shadowId})`} />

        {/* Dashed vertical separator line - positioned at ~70% from left */}
        {/* Top cap */}
        <path
          d="M770.92 36.6101V42.6101"
          stroke="rgba(0, 25, 61, 0.6)"
          strokeWidth="8"
          strokeMiterlimit="10"
        />
        {/* Middle dashed section */}
        <path
          d="M770.92 55.0701V322.78"
          stroke="rgba(0, 25, 61, 0.6)"
          strokeWidth="8"
          strokeMiterlimit="10"
          strokeDasharray="12.45 12.45"
        />
        {/* Bottom cap */}
        <path
          d="M770.92 329.01V335.01"
          stroke="rgba(0, 25, 61, 0.6)"
          strokeWidth="8"
          strokeMiterlimit="10"
        />
      </svg>

      {/* Content overlay */}
      <div
        className="absolute inset-0 pointer-events-none transition-transform duration-150"
        style={{ transform: activePress ? 'translateY(3px)' : 'none' }}
      >
        {renderContent()}
      </div>
    </button>
  );
}

export default TicketCard;
